import { useState } from 'react';
import {
  Box, Typography, Stack, Chip, Button, IconButton, Tooltip, Menu, MenuItem,
  ListItemIcon, ListItemText, alpha, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Refresh, Add, DirectionsCar, Restaurant, ShoppingCart, LocalShipping, Handyman,
  MoreHoriz, FiberManualRecord, Map as MapIcon, ExpandMore, ExpandLess, Sos,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { SERVICE_FILTERS, ServiceTab, serviceTabToBookMode, resolveDispatchBookMode, dispatchBookCopy, type DispatchBookMode } from './dispatchTypes';
import { DISPATCH } from './dispatchTheme';

export type { DispatchBookMode };

interface DispatchTopBarProps {
  serviceFilter: ServiceTab;
  onServiceFilterChange: (service: ServiceTab) => void;
  activeCount: number;
  pendingCount: number;
  onRefresh: () => void;
  enabledBookModes: DispatchBookMode[];
  onBook: (mode: DispatchBookMode) => void;
  statsExpanded?: boolean;
  onToggleStats?: () => void;
}

const SERVICE_ICONS: Partial<Record<ServiceTab, React.ReactNode>> = {
  transportation: <DirectionsCar sx={{ fontSize: 16 }} />,
  food: <Restaurant sx={{ fontSize: 16 }} />,
  grocery: <ShoppingCart sx={{ fontSize: 16 }} />,
  porter: <LocalShipping sx={{ fontSize: 16 }} />,
  on_demand: <Handyman sx={{ fontSize: 16 }} />,
};

const BOOK_OPTIONS: { mode: DispatchBookMode; icon: React.ReactNode }[] = [
  { mode: 'ride', icon: <DirectionsCar fontSize="small" /> },
  { mode: 'food', icon: <Restaurant fontSize="small" /> },
  { mode: 'grocery', icon: <ShoppingCart fontSize="small" /> },
  { mode: 'porter', icon: <LocalShipping fontSize="small" /> },
  { mode: 'on_demand', icon: <Handyman fontSize="small" /> },
];

export default function DispatchTopBar({
  serviceFilter,
  onServiceFilterChange,
  activeCount,
  pendingCount,
  onRefresh,
  enabledBookModes,
  onBook,
  statsExpanded,
  onToggleStats,
}: DispatchTopBarProps) {
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('md'));
  const [bookAnchor, setBookAnchor] = useState<null | HTMLElement>(null);

  const activeBookMode = resolveDispatchBookMode(serviceFilter, enabledBookModes);
  const primary = BOOK_OPTIONS.find((o) => o.mode === activeBookMode) || BOOK_OPTIONS[0];
  const primaryCopy = dispatchBookCopy(primary.mode);
  const moreOptions = BOOK_OPTIONS.filter((o) => o.mode !== primary.mode && enabledBookModes.includes(o.mode));
  const visibleServiceFilters = SERVICE_FILTERS.filter((f) => {
    if (f.value === 'all') return true;
    const mode = serviceTabToBookMode(f.value);
    return mode != null && enabledBookModes.includes(mode);
  });

  return (
    <Box sx={{ background: DISPATCH.headerGradient, color: '#fff', px: { xs: 1.5, sm: 2, md: 2.5 }, py: { xs: 1.5, md: 2 }, boxShadow: DISPATCH.shadowLg, flexShrink: 0 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1.25} minWidth={0}>
            <Box sx={{ width: { xs: 40, md: 44 }, height: { xs: 40, md: 44 }, borderRadius: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.14)', flexShrink: 0 }}>
              <MapIcon />
            </Box>
            <Box minWidth={0}>
              <Typography variant={narrow ? 'subtitle1' : 'h6'} fontWeight={800} letterSpacing="-0.02em" lineHeight={1.2} noWrap>
                Dispatch Center
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.82, display: { xs: 'none', sm: 'block' } }}>
                Real-time operations · assign · dispatch
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={0.75} alignItems="center" flexShrink={0}>
            <Tooltip title="SOS Command Center">
              <IconButton size="small" component={RouterLink} to="/dispatcher/sos" sx={{ color: '#fecaca', border: '1px solid rgba(252,165,165,0.35)', bgcolor: 'rgba(239,68,68,0.2)' }}>
                <Sos fontSize="small" />
              </IconButton>
            </Tooltip>
            {onToggleStats && (
              <Tooltip title={statsExpanded ? 'Hide KPIs' : 'Show KPIs'}>
                <IconButton size="small" onClick={onToggleStats} sx={{ color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}>
                  {statsExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={onRefresh} sx={{ color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}>
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          <Chip label={`${activeCount} active`} size="small" sx={{ fontWeight: 800, bgcolor: 'rgba(255,255,255,0.14)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }} />
          <Chip label={`${pendingCount} waiting`} size="small" sx={{ fontWeight: 800, bgcolor: alpha('#fbbf24', 0.25), color: '#fef3c7' }} />
          <Chip icon={<FiberManualRecord sx={{ fontSize: '8px !important', color: '#4ade80 !important' }} />} label="Live" size="small"
            sx={{ fontWeight: 800, bgcolor: 'rgba(34,197,94,0.2)', color: '#bbf7d0', border: '1px solid rgba(74,222,128,0.35)' }} />
        </Stack>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            rowGap: 0.75,
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, minWidth: 0, flex: '1 1 220px' }}>
            {visibleServiceFilters.map((f) => (
              <Button
                key={f.value}
                size="small"
                color="inherit"
                onClick={() => onServiceFilterChange(f.value)}
                startIcon={SERVICE_ICONS[f.value] as React.ReactElement | undefined}
                sx={{
                  minWidth: 'auto',
                  flexShrink: 0,
                  px: { xs: 1.25, md: 1.5 },
                  py: 0.65,
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: 2,
                  color: '#fff !important',
                  bgcolor: serviceFilter === f.value ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.08)',
                  border: '1px solid',
                  borderColor: serviceFilter === f.value ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.12)',
                  '&:hover': { bgcolor: serviceFilter === f.value ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.14)' },
                  '& .MuiButton-startIcon, & .MuiButton-startIcon > *': { color: 'inherit !important' },
                }}
              >
                {narrow ? f.short : f.label}
              </Button>
            ))}
          </Box>

          {enabledBookModes.length > 0 && (
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ ml: 'auto', flexShrink: 0 }}>
              <Tooltip title={primaryCopy.createNew}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Add sx={{ fontSize: 18 }} />}
                  onClick={() => onBook(primary.mode)}
                  sx={{
                    height: 36,
                    minHeight: 36,
                    px: 1.75,
                    py: 0,
                    width: 'auto',
                    minWidth: 'auto',
                    fontWeight: 700,
                    fontSize: '0.8125rem',
                    lineHeight: 1,
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    borderRadius: 1.5,
                    bgcolor: DISPATCH.primary,
                    color: '#fff',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
                    '&:hover': { bgcolor: DISPATCH.primaryDark, boxShadow: '0 2px 6px rgba(79,70,229,0.45)' },
                    '& .MuiButton-startIcon': { mr: 0.75, ml: 0 },
                    '& .MuiButton-startIcon > *': { color: 'inherit', fontSize: 18 },
                  }}
                >
                  {primaryCopy.newBooking}
                </Button>
              </Tooltip>
              {moreOptions.length > 0 && (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    endIcon={<MoreHoriz />}
                    onClick={(e) => setBookAnchor(e.currentTarget)}
                    sx={{
                      height: 36,
                      minHeight: 36,
                      px: 1.25,
                      py: 0,
                      minWidth: 'auto',
                      fontWeight: 700,
                      fontSize: '0.8125rem',
                      lineHeight: 1,
                      textTransform: 'none',
                      whiteSpace: 'nowrap',
                      borderRadius: 1.5,
                      color: '#fff !important',
                      borderColor: 'rgba(255,255,255,0.35) !important',
                      '&:hover': { borderColor: '#fff !important', bgcolor: 'rgba(255,255,255,0.08)' },
                      '& .MuiButton-endIcon': { ml: 0.5 },
                      '& .MuiButton-endIcon > *': { color: 'inherit !important', fontSize: 18 },
                    }}
                  >
                    More
                  </Button>
                  <Menu anchorEl={bookAnchor} open={!!bookAnchor} onClose={() => setBookAnchor(null)} PaperProps={{ sx: { minWidth: 200, borderRadius: 2.5 } }}>
                    {moreOptions.map((opt) => (
                      <MenuItem key={opt.mode} onClick={() => { onBook(opt.mode); setBookAnchor(null); }}>
                        <ListItemIcon>{opt.icon}</ListItemIcon>
                        <ListItemText>{dispatchBookCopy(opt.mode).newBooking}</ListItemText>
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
            </Stack>
          )}
        </Box>
      </Stack>
    </Box>
  );
}
